import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTable from "@toothfairy/shared-ui/AppTable";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { View, useWindowDimensions } from "react-native";
import envConfig from "../../../../../envConfig";
import sitesHelper from "../../../../API/Sites/sitesHelper";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyChoice from "../../../../Components/ToothFairyComponents/ToothFairyChoice";
import ToothFairyLoadingPlaceholder from "../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { useHistory, useParams } from "../../../../Router";
import Utility from "../../../../Utility";

const SitesList = ({ isFunctionsLoading }) => {
  const { currentTheme } = AppTheme.useTheme();
  const { width } = useWindowDimensions();
  const { getPlanConfig } = useWorkspaces();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const [showChoice, setShowChoice] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [selectedIndex, setSelectedIndex] = React.useState(null);
  const [selectedSiteId, setSelectedSiteId] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [displayedSites, setDisplayedSites] = React.useState([]);
  const [selectedSiteIds, setSelectedSiteIds] = React.useState([]);
  const [showBulkDeleteChoice, setShowBulkDeleteChoice] = React.useState(false);
  const { t } = useTranslation();
  const AppToasty = AppToast.useToast();
  const { height } = useWindowDimensions();
  const { getActiveWorkspace } = useWorkspaces();
  const history = useHistory();
  const params = useParams();
  useEffect(() => {
    const optId = params?.optId;
    if (!optId) return;
    const wsId = getActiveWorkspace()?.id;
    if (!wsId) return;
    if (isLoading) return;
    history.replace(`/workspaces/${wsId}/sites/${optId}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.optId, isLoading, getActiveWorkspace()?.id]);

  const columns = [
    {
      title: t("settings.list.id"),
      width: 0.00001,
    },
    {
      title: t("settings.list.domain"),
    },
    {
      title: t("settings.list.syncedPages"),
      width: 0.1,
    },
    {
      title: t("settings.list.status"),
      width: 0.1,
    },
    {
      title: t("settings.list.actions"),
      isAction: true,
      width: 0.1,
    },
  ];

  useEffect(() => {
    setDisplayedSites([...state?.sites]);
  }, [state?.sites]);
  const requestValidation = async (id, status) => {
    let newSites = [...state.sites];
    if (newSites.find((site) => site.id == id && site.status == "syncing")) {
      return AppToasty.show(t("settings.list.siteAlreadySyncing"), {
        type: "danger",
      });
    }
    try {
      let result;
      result = await sitesHelper.updateSite(id, [
        { property: "status", value: "pending" },
      ]);
      const _updatedObject = identifyOptByID(result?.id);
      const updateIndex = newSites.findIndex(
        (u) => u?.id === _updatedObject?.id
      );
      result.status = "syncing";
      newSites[updateIndex] = result;
      dispatch("SET_SITES", [...newSites]);
      // TRIGGER VALIDATION OF SITE
      AppToasty.show(t("settings.list.siteSyncRequestedSuccess"), {
        type: "success",
      });
    } catch (error) {
      console.error(error);
      AppToasty.show(t("settings.list.siteSyncRequestFailed"), {
        type: "danger",
      });
    }
  };
  const generatePagesCount = (u) => {
    if (u?.scapablePages) return `${u?.scrapedPagesCount}/${u?.scapablePages}`;
    else {
      return typeof u?.siteTree === "string"
        ? JSON.parse(u?.siteTree)?.length?.toString()
        : u?.siteTree?.length?.toString() || "0";
    }
  };
  const identifyOptByID = (id) => {
    return state?.sites?.find((u) => u.id === id);
  };
  const handleSiteItemSelection = ({ id, item, index }) => {
    const wsId = getActiveWorkspace()?.id;
    if (!wsId || !id) return;
    history.push(`/workspaces/${wsId}/sites/${id}`);
  };
  const loadSites = async (workspaceID) => {
    try {
      setIsLoading(true);
      let resultSites = await sitesHelper?.getSitesByWorkspaceID(workspaceID);
      resultSites = resultSites?.data?.sitesByWorkspace?.items?.filter(
        (u) => u?._deleted != true
      );
      dispatch("SET_SITES", resultSites);
    } catch (error) {
      console.error("Error loading sites:", error);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (getActiveWorkspace()?.id) {
      loadSites(getActiveWorkspace()?.id);
    }
  }, [getActiveWorkspace()?.id]);
  const handleSiteItemDeletion = async (item, index) => {
    try {
      setShowChoice(false);
      const _id = selectedSiteId || selectedItem?.id;
      if (_id) {
        const result = await sitesHelper.deleteSite(_id);
        dispatch("SET_SITES", [...state?.sites.filter((u) => u.id !== _id)]);
        AppToasty.show(t("siteDeletionSuccessCaption"), {
          type: "success",
        });
        setSelectedIndex(null);
        setSelectedSiteId(null);
        setSelectedItem(null);
      } else {
        throw new Error(t("settings.list.noItemSelected"));
      }
    } catch (error) {
      console.log(error);
      AppToasty.show(t("siteDeletionErrorCaption"), {
        type: "danger",
      });
    }
  };
  const handleBulkDeletion = async () => {
    try {
      for (const id of selectedSiteIds) {
        await sitesHelper.deleteSite(id);
      }
      dispatch("SET_SITES", [
        ...state?.sites.filter((u) => !selectedSiteIds.includes(u.id)),
      ]);
      setSelectedSiteIds([]);
      setShowBulkDeleteChoice(false);
      AppToasty.show(t("siteDeletionSuccessCaption"), {
        type: "success",
      });
    } catch (error) {
      console.log(error);
      setShowBulkDeleteChoice(false);
      AppToasty.show(t("siteDeletionErrorCaption"), {
        type: "danger",
      });
    }
  };
  const handleCreation = () => {
    const wsId = getActiveWorkspace()?.id;
    if (!wsId) return;
    history.push(`/workspaces/${wsId}/sites/new`);
  };
  const isItemActionable = (item) => {
    const _status = item?.status?.toLowerCase();
    return ["inProgress", "syncing", "inactive"].includes(_status) != true;
  };
  const wasTheLastScrapeDoneInTheLastxHours = (item, _hours_ = 24) => {
    const _item = state.sites?.find((u) => u.id === item?.id);
    let now = new Date();
    let now_string = _item?.lastScraped || "2021-07-01T00:00:00.000Z";
    let pastDate = new Date(now_string);
    let diff = now - pastDate;
    let hours = diff / (1000 * 60 * 60);
    if (hours >= _hours_) {
      return true;
    } else {
      return false;
    }
  };
  const _coreChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={t("settings.list.cancel")}
        caption={t("settings.list.deleteSiteConfirmation")}
        subCaption={t("settings.list.deleteSiteConfirmationMessage")}
        confirmCaption={t("settings.list.delete")}
        onCancelButtonPress={() => setShowChoice(false)}
        onConfirmButtonPress={() => handleSiteItemDeletion()}
        setShowChoice={setShowChoice}
        showChoice={showChoice}
      />
    );
  }, [setShowChoice, showChoice, selectedItem, setSelectedItem]);

  const _bulkDeleteChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={t("settings.list.cancelConfirmation")}
        caption={t("settings.list.deleteSelectedConfirmation", {
          defaultValue: t("settings.list.deleteSiteConfirmation"),
        })}
        subCaption={t("settings.list.deleteSiteConfirmationMessage")}
        confirmCaption={t("settings.list.delete")}
        onCancelButtonPress={() => setShowBulkDeleteChoice(false)}
        onConfirmButtonPress={handleBulkDeletion}
        setShowChoice={setShowBulkDeleteChoice}
        showChoice={showBulkDeleteChoice}
      />
    );
  }, [showBulkDeleteChoice, selectedSiteIds]);

  return (
    <View
      style={{
        minWidth: "100%",
        paddingLeft: 10,
        flex: 1,
        overflow: isLoading && "hidden",
      }}
    >
      {getPlanConfig()?.isSitesBindingEnabled ? (
        <>
          {isLoading ? (
            <ToothFairyLoadingPlaceholder
              backgroundColor={currentTheme?.theme?.softBackgroundColor}
              visible={true}
              containerStyle={{
                backgroundColor: currentTheme?.theme?.transparent,
                minHeight: 250,
              }}
              caption={t("settings.list.loadingSites")}
            />
          ) : (
            <>
              {displayedSites.length > 0 ? (
                <AppTable
                  isSelectable
                  showPagination
                  itemsPerPage={50}
                  hideSearch={false}
                  searchPlaceholder={t("settings.toolbar.searchDomains")}
                  onSelectionChange={(selectedItems) => {
                    setSelectedSiteIds(selectedItems?.map((u) => u?.id) || []);
                  }}
                  bulkActions={[
                    {
                      id: "DELETE",
                      label: t("settings.list.delete"),
                      icon: AppAssets.icons.delete,
                      isDestructive: true,
                    },
                  ]}
                  onBulkActionPress={(id) => {
                    if (id === "DELETE" && selectedSiteIds?.length > 0)
                      setShowBulkDeleteChoice(true);
                  }}
                  actionButtons
                  mapIconWithStatus={(item) => {
                    const _status = item?.status?.toLowerCase();
                    if (_status == "active" || _status == "syncing")
                      return AppAssets.icons.reload;
                    else return AppAssets.icons.activeModel;
                  }}
                  mainActionLabel={t("settings.list.view")}
                  showDropDownMenuForActions
                  onClickMainAction={(item, index) => {
                    handleSiteItemSelection({
                      id: item?.id,
                      item: item,
                      index: index,
                    });
                  }}
                  actionButtonStyle={{
                    minWidth: 20,
                  }}
                  onActionItemPress={async (id, item, index) => {
                    const _status = item?.status?.toLowerCase();
                    if (id === "EDIT") {
                      handleSiteItemSelection({
                        id: item?.id,
                        item: item,
                        index: index,
                      });
                    } else if (id === "DELETE") {
                      if (!isItemActionable(item)) {
                        AppToasty.show(
                          t("settings.list.siteDeleteWhileActive", {
                            status: item?.status,
                          }),
                          {
                            type: "danger",
                          }
                        );
                        return;
                      } else {
                        setSelectedItem(item);
                        setSelectedIndex(index);
                        setSelectedSiteId(item?.id);
                        setShowChoice(true);
                      }
                    } else if (id === "SYNC") {
                      if (isItemActionable(item)) {
                        if (
                          _status == "active" &&
                          !wasTheLastScrapeDoneInTheLastxHours(
                            item,
                            envConfig.REACT_APP_ENV == "dev" ? 0 : 24
                          )
                        )
                          AppToasty.show(t("settings.list.siteSyncTooSoon"), {
                            type: "danger",
                          });
                        else if (_status == "pending") {
                          let newSites = [...state.sites];
                          const result = await sitesHelper.updateSite(
                            item?.id,
                            [{ property: "status", value: "syncing" }]
                          );
                          const _updatedObject = identifyOptByID(result?.id);
                          const updateIndex = newSites.findIndex(
                            (u) => u?.id === _updatedObject?.id
                          );
                          newSites[updateIndex] = result;
                          dispatch("SET_SITES", [...newSites]);
                          AppToasty.show(
                            t("settings.list.siteSyncRequestedSuccess"),
                            {
                              type: "success",
                            }
                          );
                        } else {
                          requestValidation(item?.id, _status);
                        }
                      } else {
                        AppToasty.show(
                          t("settings.list.siteSyncCannotRequest", {
                            status: item?.status,
                          }),
                          {
                            type: "danger",
                          }
                        );
                      }
                    } else if (id === "OWNERSHIP") {
                      if (isItemActionable(item)) {
                        let newSites = [...state.sites];
                        const result = await sitesHelper.updateSite(item?.id, [
                          { property: "status", value: "readyForValidation" },
                        ]);
                        const _updatedObject = identifyOptByID(result?.id);
                        const updateIndex = newSites.findIndex(
                          (u) => u?.id === _updatedObject?.id
                        );
                        newSites[updateIndex] = result;
                        dispatch("SET_SITES", [...newSites]);
                        AppToasty.show(
                          t("settings.list.siteValidationRequestedSuccess"),
                          {
                            type: "success",
                          }
                        );
                      } else {
                        AppToasty.show(
                          t("settings.list.siteValidationCannotRequest", {
                            status: item?.status,
                          }),
                          {
                            type: "danger",
                          }
                        );
                      }
                    }
                  }}
                  actions={[
                    {
                      label: t("settings.list.edit"),
                      id: "EDIT",
                      icon: AppAssets.icons.edit,
                    },
                    {
                      label: t("settings.list.sync"),
                      id: "SYNC",
                      icon: AppAssets.icons.reload,
                    },
                    {
                      label: t("settings.list.dns"),
                      id: "OWNERSHIP",
                      icon: AppAssets.icons.admin,
                    },
                    {
                      label: t("settings.list.delete"),
                      id: "DELETE",
                      icon: AppAssets.icons.delete,
                    },
                  ]}
                  isItemActionable={(item) =>
                    isItemActionable({
                      item: item,
                    })
                  }
                  style={{
                    zIndex: -1,
                    marginTop: 15,
                    height:
                      displayedSites?.length >= 50 ? height - 95 : height - 75,
                    paddingRight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                  headers={columns}
                  items={displayedSites?.map((u) => {
                    return {
                      id: u?.id || " ",
                      url: u?.url || " ",
                      pagesCount: generatePagesCount(u),
                      status: Utility.camelCaseToSpaced(u?.status) || " ",
                      actions: " ",
                    };
                  })}
                >
                  <ToothFairyButton
                    title={t("settings.toolbar.addDomain")}
                    onPress={handleCreation}
                  />
                </AppTable>
              ) : (
                <AppPlaceholderView
                  wrapperStyle={{
                    width: "100%",
                    height: "100%",
                    marginVertical: 20,
                    marginHorizontal: "auto",
                    flex: 1,
                  }}
                  size={30}
                  icon={AppAssets.icons.web}
                  mainCaption={t("settings.list.noSitesFound")}
                  subCaption={t("settings.list.noSitesFoundSub")}
                  actionLabel={t("settings.toolbar.addDomain")}
                  onActionPress={handleCreation}
                />
              )}
            </>
          )}
        </>
      ) : (
        <AppPlaceholderView
          wrapperStyle={{
            width: "100%",
            height: "100%",
            marginVertical: 20,
            marginHorizontal: "auto",
            flex: 1,
          }}
          size={30}
          icon={AppAssets.icons.orbit}
          mainCaption={t("settings.list.emptyPlaceholder")}
          subCaption={t("settings.list.sitesNotAvailable")}
        />
      )}
      {_coreChoice}
      {_bulkDeleteChoice}
    </View>
  );
};

export default SitesList;
